import { format, differenceInDays, startOfWeek, endOfWeek } from 'date-fns';
import { enUS, tr } from 'date-fns/locale';
import { intToDate, intToTimeStr } from '@/helpers/dateTime';

/**
 * Determines the appropriate X-axis format based on date range
 * @param {Date} startDate - Start date of the range
 * @param {Date} endDate - End date of the range
 * @param {string} selectedDate - Selected date option (e.g., 'today', 'thisWeek')
 * @param {string} groupBy - User selected grouping ('date', 'timeOfDay', 'weekday')
 * @returns {string} - Format type: 'hourly', 'daily', 'weekly', 'monthly', 'weekday'
 */
export function getXAxisFormatType(startDate, endDate, selectedDate, groupBy = 'date') {
  // 1. User selected grouping overrides all default behavior
  if (groupBy === 'timeOfDay') {
    return 'hourly';
  }
  if (groupBy === 'weekday') {
    return 'weekday';
  }
  // groupBy === 'date' continues to default logic below

  // 2. Today -> show hours by default
  if (selectedDate === 'today') {
    return 'hourly';
  }

  // 3. This week, last seven days, this month, last month, or last 30 days -> show daily dates
  const selectedDateLower = selectedDate?.toLowerCase();
  if (
    selectedDateLower === 'thisweek' ||
    selectedDateLower === 'lastsevendays' ||
    selectedDateLower === 'thismonth' ||
    selectedDateLower === 'lastmonth' ||
    selectedDateLower === 'lastthirtydays'
  ) {
    return 'daily';
  }

  // 4. Calculate date difference for other cases
  const daysDiff = differenceInDays(endDate, startDate);

  // Single day -> show hours
  if (daysDiff === 0) {
    return 'hourly';
  }

  // 5. Up to 1 month (31 days) -> show daily dates
  if (daysDiff <= 31) {
    return 'daily';
  }

  // 6. Up to 6 months (180 days) -> show weeks
  if (daysDiff <= 180) {
    return 'weekly';
  }

  // 7. More than 6 months -> show months
  return 'monthly';
}

/**
 * Formats X-axis label based on format type
 * @param {object} item - Data item with operationDay and/or hour
 * @param {string} formatType - Format type from getXAxisFormatType
 * @param {string} locale - Locale ('en' or 'tr')
 * @returns {string} - Formatted label
 */
export function formatXAxisLabel(item, formatType, locale = 'en') {
  const isTr = locale === 'tr';
  const dateLocale = isTr ? tr : enUS;

  switch (formatType) {
    case 'hourly':
      // Convert integer hour (e.g., 800) to time string (e.g., "08:00")
      return intToTimeStr(item.hour);

    case 'daily': {
      const date = intToDate(item.operationDay);
      const pattern = isTr ? 'dd.MM' : 'MM.dd';
      return format(date, pattern, { locale: dateLocale });
    }

    case 'weekly': {
      const date = intToDate(item.operationDay);
      const weekStart = startOfWeek(date, { weekStartsOn: 1 });
      const weekEnd = endOfWeek(date, { weekStartsOn: 1 });
      const startMonth = format(weekStart, 'MMM', { locale: dateLocale });
      const endMonth = format(weekEnd, 'MMM', { locale: dateLocale });
      const startDay = format(weekStart, 'd');
      const endDay = format(weekEnd, 'd');
      // A week running into the next month names both ("Sep 29-Oct 5", not "Sep 29-5").
      if (startMonth !== endMonth) return `${startMonth} ${startDay}-${endMonth} ${endDay}`;
      return `${startMonth} ${startDay}-${endDay}`;
    }

    case 'monthly': {
      const date = intToDate(item.operationDay);
      return format(date, 'MMM yyyy', { locale: dateLocale });
    }

    case 'weekday': {
      const date = intToDate(item.operationDay);
      return format(date, 'EEEE', { locale: dateLocale }); // Full weekday name
    }

    default:
      return '';
  }
}

/**
 * Reorders hourly items so that an overnight operation window reads naturally on the
 * x-axis. When the window crosses midnight (fromHour > toHour), the backend bundles
 * the next calendar day's early hours under the same operationDay; sorted ascending
 * those early hours (e.g. 00:00) land BEFORE the evening ones (20:00–23:00). This
 * helper pushes any hour below fromHour to the end of its operationDay group so the
 * sequence matches the operation itself (e.g. 20:00, 21:00, 22:00, 23:00, 00:00).
 *
 * Non-hourly data, missing filter hours, or a non-overnight window pass through.
 *
 * @param {Array} items - Hourly rows carrying operationDay and hour
 * @param {string} formatType - Format type from getXAxisFormatType
 * @param {number} fromHour - Operation window start as HHMM int (e.g. 1900)
 * @param {number} toHour - Operation window end as HHMM int (e.g. 30 for 00:30)
 * @returns {Array} - Reordered items (new array; input untouched)
 */
// The filters pass fromHour/toHour as "HH:MM" strings (ChartHeader's <input type="time">),
// but item.hour comes from the backend as an HHMM integer (e.g. 2000 for 20:00). Normalize
// both sides to HHMM integers before comparing.
function toHHMM(value) {
  if (value == null || value === '') return null;
  if (typeof value === 'number') return value;
  const match = String(value).match(/^(\d{1,2}):(\d{2})/);
  if (!match) return Number.isFinite(Number(value)) ? Number(value) : null;
  return Number(match[1]) * 100 + Number(match[2]);
}

/**
 * Converts an operation window (fromHour, toHour) to its length in decimal hours.
 * Handles overnight windows that wrap past midnight (e.g. 19:00 → 00:30 = 5.5h).
 * Returns null when either endpoint is missing or unparseable.
 *
 * @param {number|string} fromHour - Window start (HHMM int or "HH:MM" string)
 * @param {number|string} toHour - Window end (HHMM int or "HH:MM" string)
 * @returns {number|null}
 */
export function calculateOperationHours(fromHour, toHour) {
  const from = toHHMM(fromHour);
  const to = toHHMM(toHour);
  if (from == null || to == null) return null;
  const toDecimal = (hhmm) => Math.floor(hhmm / 100) + (hhmm % 100) / 60;
  const fromH = toDecimal(from);
  const toH = toDecimal(to);
  if (fromH === toH) return 0;
  return fromH < toH ? toH - fromH : 24 - fromH + toH;
}

/**
 * Returns a comparator for hour values (numbers or numeric strings) that respects
 * an overnight operation window. When the window crosses midnight (fromHour > toHour),
 * hours before fromHour belong to the end of the sequence; when it doesn't, sort
 * ascending as usual.
 *
 * @param {number|string} fromHour - Window start (HHMM int or "HH:MM" string)
 * @param {number|string} toHour - Window end (HHMM int or "HH:MM" string)
 * @returns {(a, b) => number} Comparator usable with Array.prototype.sort
 */
export function hourWindowComparator(fromHour, toHour) {
  const from = toHHMM(fromHour);
  const to = toHHMM(toHour);
  const overnight = from != null && to != null && from > to;
  return (a, b) => {
    const aNum = Number(a);
    const bNum = Number(b);
    if (overnight) {
      const aAfter = aNum >= from;
      const bAfter = bNum >= from;
      if (aAfter !== bAfter) return aAfter ? -1 : 1;
    }
    return aNum - bNum;
  };
}

/**
 * Reorders hourly items so that an overnight operation window reads naturally on the
 * x-axis. When the window crosses midnight (fromHour > toHour), the backend bundles
 * the next calendar day's early hours under the same operationDay; sorted ascending
 * those early hours (e.g. 00:00) land BEFORE the evening ones (20:00–23:00). This
 * helper pushes any hour below fromHour to the end of its operationDay group so the
 * sequence matches the operation itself (e.g. 20:00, 21:00, 22:00, 23:00, 00:00).
 *
 * Non-hourly data, missing filter hours, or a non-overnight window pass through.
 *
 * @param {Array} items - Hourly rows carrying operationDay and hour
 * @param {string} formatType - Format type from getXAxisFormatType
 * @param {number|string} fromHour - Operation window start (HHMM int or "HH:MM" string)
 * @param {number|string} toHour - Operation window end (HHMM int or "HH:MM" string)
 * @returns {Array} - Reordered items (new array; input untouched)
 */
export function sortHourlyItemsByWindow(items, formatType, fromHour, toHour) {
  const from = toHHMM(fromHour);
  const to = toHHMM(toHour);
  if (
    formatType !== 'hourly' ||
    !Array.isArray(items) ||
    from == null ||
    to == null ||
    from <= to
  ) {
    return items;
  }
  const compareHours = hourWindowComparator(fromHour, toHour);
  const byDay = new Map();
  items.forEach((item) => {
    const key = item?.operationDay ?? 0;
    if (!byDay.has(key)) byDay.set(key, []);
    byDay.get(key).push(item);
  });
  const sortedDays = [...byDay.keys()].sort((a, b) => Number(a) - Number(b));
  return sortedDays.flatMap((day) => [...byDay.get(day)].sort((a, b) => compareHours(a.hour, b.hour)));
}

/**
 * Groups data items by the appropriate key based on format type
 * @param {Array} items - Array of data items
 * @param {string} formatType - Format type from getXAxisFormatType
 * @param {string} groupBy - User selected grouping ('date', 'timeOfDay', 'weekday')
 * @returns {object} - Grouped data by key
 */
export function groupDataByFormatType(items, formatType, groupBy = 'date') {
  const grouped = {};

  items.forEach((item) => {
    let key;

    switch (formatType) {
      case 'hourly':
        // If groupBy is 'timeOfDay', group only by hour (aggregate across all days)
        // Otherwise, group by operationDay + hour (separate for each day)
        key = groupBy === 'timeOfDay' ? item.hour : `${item.operationDay}_${item.hour}`;
        break;

      case 'daily':
        // Group by operationDay
        key = item.operationDay;
        break;

      case 'weekly': {
        // Group by week number and year
        const date = intToDate(item.operationDay);
        const weekStart = startOfWeek(date, { weekStartsOn: 1 });
        key = format(weekStart, 'yyyy-ww');
        break;
      }

      case 'monthly': {
        // Group by year and month
        const date = intToDate(item.operationDay);
        key = format(date, 'yyyy-MM');
        break;
      }

      case 'weekday': {
        // Group by weekday (0-6)
        const date = intToDate(item.operationDay);
        key = format(date, 'EEEE'); // Use weekday name as key
        break;
      }

      default:
        key = item.operationDay;
    }

    if (!grouped[key]) {
      grouped[key] = [];
    }

    grouped[key].push(item);
  });

  return grouped;
}

/**
 * Aggregates grouped data items.
 *
 * A null/undefined field is "no value", not 0: the backend sends null where a metric is
 * undefined for the row (no planned pickups → no deviation, no finished trips → no OTP).
 * Such inputs are skipped when summing, averaging or taking the max, and a field whose
 * every input is empty aggregates to null so the chart can leave a gap in the line
 * instead of drawing 0 (VSER-14215). Counts are never null, so their sums are unchanged.
 * @param {Array} groupedItems - Array of items in the same group
 * @param {Array} sumFields - Fields to sum (e.g., ['delayedPickups', 'totalTrips'])
 * @param {Array} avgFields - Fields to average (e.g., ['pickupOtp', 'avgDeviation'])
 * @param {Array} maxFields - Fields to get maximum value (e.g., ['pickupMaxDelay', 'dropoffMaxDelay'])
 * @param {Array} weightedAvgFields - Fields to calculate weighted average using 'trips' as weight (e.g., ['dropoffOtp'])
 * @returns {object} - Aggregated item
 */
export function aggregateGroupedData(
  groupedItems,
  sumFields = [],
  avgFields = [],
  maxFields = [],
  weightedAvgFields = [],
) {
  if (groupedItems.length === 0) return null;

  const aggregated = { ...groupedItems[0] };
  const valuesOf = (field) =>
    groupedItems.filter((item) => item[field] !== null && item[field] !== undefined).map((item) => item[field]);
  const sum = (values) => values.reduce((total, value) => total + value, 0);

  sumFields.forEach((field) => {
    const values = valuesOf(field);
    aggregated[field] = values.length ? sum(values) : null;
  });

  avgFields.forEach((field) => {
    const values = valuesOf(field);
    aggregated[field] = values.length ? Math.round(sum(values) / values.length) : null;
  });

  maxFields.forEach((field) => {
    const values = valuesOf(field);
    aggregated[field] = values.length ? Math.max(...values) : null;
  });

  // Weighted average: (Σ(value × trips)) / Σ(trips)
  weightedAvgFields.forEach((field) => {
    const itemsWithValue = groupedItems.filter(
      (item) => item[field] !== null && item[field] !== undefined && item.trips > 0,
    );
    if (itemsWithValue.length === 0) {
      aggregated[field] = null;
      return;
    }
    const totalWeightedValue = itemsWithValue.reduce((total, item) => total + item[field] * item.trips, 0);
    const totalTrips = itemsWithValue.reduce((total, item) => total + item.trips, 0);
    aggregated[field] = totalTrips > 0 ? Math.round(totalWeightedValue / totalTrips) : null;
  });

  return aggregated;
}
